import { Link } from "react-router-dom";
import TopNav from "@/components/TopNav";
import {
  BookOpenCheck,
  BarChart3,
  Users,
  ShieldCheck,
  Timer,
  Sparkles,
  ArrowRight,
  CheckCircle2,
  Building2,
} from "lucide-react";

const HERO_IMG =
  "https://images.pexels.com/photos/35545648/pexels-photo-35545648.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const FEATURE_STUDENT_IMG =
  "https://images.pexels.com/photos/8197503/pexels-photo-8197503.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const FEATURE_TEACHER_IMG =
  "https://images.pexels.com/photos/8423069/pexels-photo-8423069.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const COMMUNITY_IMG =
  "https://images.pexels.com/photos/7972324/pexels-photo-7972324.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

function Stat({ value, label, testid }) {
  return (
    <div className="flex flex-col" data-testid={testid}>
      <span className="font-display text-3xl sm:text-4xl font-black text-slate-900">{value}</span>
      <span className="text-xs uppercase tracking-[0.2em] font-bold text-indigo-600 mt-1">{label}</span>
    </div>
  );
}

function FeatureCard({ icon: Icon, title, desc, tone = "indigo" }) {
  const tones = {
    indigo: "bg-indigo-50 text-indigo-600",
    amber: "bg-amber-50 text-amber-600",
    emerald: "bg-emerald-50 text-emerald-600",
    rose: "bg-rose-50 text-rose-600",
    sky: "bg-sky-50 text-sky-600",
    violet: "bg-violet-50 text-violet-600",
  };
  return (
    <div className="qs-card p-6">
      <div className={`w-11 h-11 rounded-xl grid place-items-center ${tones[tone]}`}>
        <Icon className="w-5 h-5" />
      </div>
      <h3 className="mt-4 text-lg font-semibold text-slate-900">{title}</h3>
      <p className="mt-1.5 text-sm leading-relaxed text-slate-600">{desc}</p>
    </div>
  );
}

export default function Landing() {
  return (
    <div className="min-h-screen bg-slate-50" data-testid="landing-page">
      <TopNav />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 qs-grid-bg pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-16 pb-20 lg:pt-24 lg:pb-28 relative">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7 rise-in">
              <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-bold text-indigo-600 bg-white border border-indigo-100 rounded-full px-3 py-1">
                <Sparkles className="w-3.5 h-3.5" /> Built for schools, teachers & learners
              </span>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-black text-slate-900 mt-5">
                Smarter exams.
                <br />
                <span className="text-gradient-indigo">Sharper results.</span>
              </h1>
              <p className="mt-6 text-base sm:text-lg leading-relaxed text-slate-600 max-w-xl">
                Prismiq is the all-in-one platform that helps educators craft rich exams, run
                timed tests at scale, and gives students crystal-clear performance analytics —
                without the busywork.
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-3">
                <Link to="/register" className="btn-primary" data-testid="hero-cta-signup">
                  Start free <ArrowRight className="w-4 h-4" />
                </Link>
                <Link to="/login" className="btn-ghost" data-testid="hero-cta-login">
                  I already have an account
                </Link>
              </div>

              <div className="mt-12 grid grid-cols-3 gap-6 max-w-lg">
                <Stat value="120K+" label="Tests Delivered" testid="stat-tests" />
                <Stat value="98%" label="Uptime" testid="stat-uptime" />
                <Stat value="24/7" label="Access" testid="stat-access" />
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="relative">
                <div className="absolute -top-6 -left-6 w-40 h-40 bg-indigo-200/40 rounded-full blur-3xl" />
                <div className="absolute -bottom-6 -right-6 w-56 h-56 bg-amber-200/40 rounded-full blur-3xl" />
                <img
                  src={HERO_IMG}
                  alt="Students studying with laptops"
                  className="relative rounded-2xl shadow-xl border border-white/60 w-full object-cover aspect-[4/5]"
                  data-testid="hero-image"
                />
                <div className="absolute bottom-4 left-4 right-4 bg-white/95 backdrop-blur border border-slate-200 rounded-xl p-4 shadow-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs uppercase tracking-[0.2em] font-bold text-indigo-600">Live now</p>
                      <p className="text-sm font-semibold text-slate-800 mt-1">Algebra Mid-Term · 45 min</p>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-display font-black text-slate-900">27:14</p>
                      <p className="text-xs text-slate-500">time remaining</p>
                    </div>
                  </div>
                  <div className="mt-3 qs-progress">
                    <span style={{ width: "62%" }} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="py-20 lg:py-24 bg-white border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="max-w-2xl">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-indigo-600">Everything you need</p>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold text-slate-900 mt-3">
              A complete workflow — from question bank to insights.
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
            <FeatureCard icon={BookOpenCheck} tone="indigo" title="Rich Exam Authoring" desc="Build MCQ or descriptive tests, set durations, passing marks, and publish to classes in minutes." />
            <FeatureCard icon={Timer} tone="rose" title="Focus-mode Test Runner" desc="A distraction-free interface with a prominent timer, question navigator, and auto-grading on submit." />
            <FeatureCard icon={BarChart3} tone="emerald" title="Performance Analytics" desc="Students see subject-level trends and pass rates. Teachers get class-level attempt insights." />
            <FeatureCard icon={Users} tone="amber" title="Classroom Management" desc="Group students into classes, assign exams, and track completion rates on a single screen." />
            <FeatureCard icon={ShieldCheck} tone="sky" title="Role-Based Access" desc="Fine-grained access for Admins, Teachers and Students — everyone sees exactly what they should." />
            <FeatureCard icon={Building2} tone="violet" title="Multi-Institution" desc="Onboard schools and colleges as tenants and manage platform-wide analytics from one dashboard." />
          </div>
        </div>
      </section>

      {/* Roles */}
      <section id="roles" className="py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 space-y-16">
          {[
            {
              tag: "Students",
              title: "Learn faster with actionable feedback.",
              desc: "Take timed exams, get instant scores, and understand exactly where you're improving with subject-level analytics.",
              img: FEATURE_STUDENT_IMG,
              bullets: ["Instant scoring", "Subject trends", "Retake practice tests"],
              cta: "Sign in as student",
              testid: "role-student",
            },
            {
              tag: "Teachers",
              title: "Craft exams that measure what matters.",
              desc: "Mix MCQ + descriptive, assign to a class, and let auto-grading handle the busywork so you can focus on teaching.",
              img: FEATURE_TEACHER_IMG,
              bullets: ["Question bank", "Class assignment", "Attempt insights"],
              cta: "Explore teacher tools",
              testid: "role-teacher",
              reverse: true,
            },
            {
              tag: "Institutions",
              title: "Scale exam operations across your campus.",
              desc: "Multi-institution onboarding, platform analytics, and a permission model built for education leadership.",
              img: COMMUNITY_IMG,
              bullets: ["Tenant onboarding", "User & role management", "Platform-wide analytics"],
              cta: "Talk to sales",
              testid: "role-institution",
            },
          ].map((r) => (
            <div key={r.tag} className={`grid lg:grid-cols-12 gap-10 items-center ${r.reverse ? "lg:[&>*:first-child]:order-2" : ""}`} data-testid={r.testid}>
              <div className="lg:col-span-6">
                <img src={r.img} alt={r.tag} className="rounded-2xl w-full aspect-[16/11] object-cover shadow-sm border border-slate-200" />
              </div>
              <div className="lg:col-span-6">
                <p className="text-xs uppercase tracking-[0.2em] font-bold text-indigo-600">For {r.tag}</p>
                <h3 className="font-display text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold text-slate-900 mt-3">{r.title}</h3>
                <p className="mt-4 text-slate-600 leading-relaxed max-w-xl">{r.desc}</p>
                <ul className="mt-5 space-y-2">
                  {r.bullets.map((b) => (
                    <li key={b} className="flex items-center gap-2 text-slate-700 text-sm">
                      <CheckCircle2 className="w-4 h-4 text-emerald-500" /> {b}
                    </li>
                  ))}
                </ul>
                <Link to="/login" className="btn-primary mt-6 text-sm">
                  {r.cta} <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section id="pricing" className="pb-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="qs-card p-10 sm:p-14 text-center">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-indigo-600">Get started</p>
            <h3 className="font-display text-3xl sm:text-4xl tracking-tight font-black text-slate-900 mt-3">
              Ready to run your first exam?
            </h3>
            <p className="mt-4 text-slate-600 max-w-2xl mx-auto">
              Create your account and try Prismiq with sample tests already loaded. No credit card required.
            </p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
              <Link to="/register" className="btn-primary" data-testid="cta-signup-bottom">
                Create free account <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/login" className="btn-ghost" data-testid="cta-login-bottom">
                Log in
              </Link>
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-slate-200 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex flex-col sm:flex-row justify-between gap-3 text-sm text-slate-500">
          <p>© 2026 Prismiq. All rights reserved.</p>
          <p>Powered by Emergent.</p>
        </div>
      </footer>
    </div>
  );
}
